import React, { useState } from 'react';
import { copyFor } from '../constants/serviceAreaCopy';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ─────────────────────────────────────────────────────────────────────────────
// THE COVERAGE SEARCH. One component, two surfaces.
//
// 🔑 IT RENDERS THE RULE; IT DOES NOT DEFINE IT. Every answer comes from
// GET /api/zip/check/:zip, which is the same endpoint the borrower application
// uses — so a partner and a borrower cannot be told different things about the
// same ZIP.
//
// ⚠️ FOUR ANSWERS. eligible · not eligible · not served (with a licence
// standing) · couldn't check. The fourth is not a refusal and must never be
// rendered as one: the reference data omits PO-box-only ZIPs, so "we cannot
// confirm" is a gap on our side.
//
// ⚠️ NOTHING A PARTNER MUST NOT SEE TRAVELS HERE. The exception REASONS and
// decided_by live on an admin-only route and never on this payload, so there is
// nothing to hide behind a render condition.
// ─────────────────────────────────────────────────────────────────────────────
// ⚠️ NO `detail` PROP. It gated the diagnostic rows, which are gone; the one
// remaining piece of staff detail is the licence note, and that is already
// admin-only by virtue of who can supply it. A prop that no longer switches
// anything is a prop somebody re-wires to the wrong thing later.
export default function CoverageSearch({ licenceNote = null, onAnswer = null }) {
  const [zip, setZip] = useState('');
  const [answer, setAnswer] = useState(null);
  const [busy, setBusy] = useState(false);

  const look = async (z) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/zip/check/${z}`);
      // ⚠️ A 429 IS AN OUTAGE FROM THE USER'S SIDE, not a verdict. They have
      // learned nothing about the ZIP and must not be told it is outside.
      // ⚠️ THE FAILURE PATHS REPORT TOO. Returning early without clearing
      // would leave the PREVIOUS ZIP's counties highlighted under a new,
      // unrelated answer — a stale highlight reads as a current one.
      if (r.status === 429) {
        const a = { confirmable: false, reason: 'lookup_unavailable' };
        setAnswer(a); if (onAnswer) onAnswer(a); return;
      }
      const body = await r.json();
      setAnswer(body);
      // 🔑 REPORTED UP so the map can mark the counties this ZIP falls in.
      if (onAnswer) onAnswer(body);
    } catch (_) {
      const a = { confirmable: false, reason: 'lookup_unavailable' };
      setAnswer(a); if (onAnswer) onAnswer(a);
    } finally { setBusy(false); }
  };

  const submit = (e) => {
    e.preventDefault();
    const z = zip.trim().replace(/\D/g, '').slice(0, 5);
    if (z.length === 5) look(z);
  };

  const tone = !answer ? null
    : answer.confirmable === false ? { bg: '#f9fafb', fg: '#6b7280', bd: '#e5e7eb' }
    : answer.eligible ? { bg: '#f4fee8', fg: '#2d6a0a', bd: '#d4f99a' }
    : { bg: '#fff7ed', fg: '#c2410c', bd: '#fed7aa' };

  return (
    // ⚠️ NO CARD CHROME OF ITS OWN. This renders inside the page's Coverage
    // card, which supplies the border, the radius and the heading — a nested
    // card and a second heading is what it looked like when this component was
    // bolted on above the page instead of placed in it.
    <div>
      <form onSubmit={submit} style={{ display: 'flex', gap: 8 }}>
        <input
          value={zip}
          onChange={(e) => { setZip(e.target.value); setAnswer(null); if (onAnswer) onAnswer(null); }}
          placeholder="ZIP code"
          inputMode="numeric"
          maxLength={5}
          style={{ flex: 1, padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 10, fontSize: 14, outline: 'none' }}
        />
        <button type="submit" disabled={busy || zip.trim().length < 5}
          style={{ padding: '9px 18px', background: busy || zip.trim().length < 5 ? '#e5e7eb' : '#3a6ad9',
            color: busy || zip.trim().length < 5 ? '#9ca3af' : '#fff', border: 'none', borderRadius: 10,
            fontWeight: 700, fontSize: 13, cursor: busy ? 'wait' : 'pointer' }}>
          {busy ? 'Checking…' : 'Check'}
        </button>
      </form>

      {answer && (
        <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 10, background: tone.bg, border: `1px solid ${tone.bd}` }}>
          <div style={{ fontSize: 13, color: tone.fg, lineHeight: 1.5 }}>{copyFor(answer)}</div>

          {/* ── 🚨 THE DIAGNOSTIC ROWS ARE GONE ───────────────────────────
              They printed State / Reason / Counties / Rule under every answer
              on the staff view — `Reason: non_rural_county` is an internal
              identifier, and a person reading a result does not need the
              machine's working shown to them. The answer above is the answer.

              ⚠️ NOTHING WAS LOST THAT ISN'T STILL REACHABLE. The publication
              year is on the map's face beneath it, the county basis is on the
              map's hover, and the full basis including exception reasons and
              who decided them is at GET /api/admin/service-area/basis. The
              endpoint still returns all of it — this is a rendering decision,
              not a narrowing of the payload.

              🔑 THE LICENCE NOTE STAYS. It is the one piece of staff detail
              that is ACTIONABLE rather than diagnostic: Partner Success relays
              it in a conversation. It arrives as a prop the admin page
              supplies; this component never fetches it, so a partner mount has
              no path to one. */}
          {licenceNote && answer.standing === 'pending' && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${tone.bd}`, fontSize: 11.5, color: '#c2410c', lineHeight: 1.7 }}>
              Internal — not partner-facing: {licenceNote}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
